'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Settings2, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { timeAgo, cn } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import {
  getNews, getAdSlots, getNewsFeeds, getNewsFeedPreferences, saveNewsFeedPreferences,
} from '@/lib/api-client';
import type { NewsArticle, AdSlot, NewsFeedPublic } from '@/lib/api-client';
import SidebarAd from '@/components/layout/SidebarAd';
import InFeedAd from '@/components/layout/InFeedAd';
import { NewsImage } from '@/components/ui/FinancePattern';
import { useAuth } from '@/context/AuthContext';
import MarketHolidaysStrip from '@/components/news/MarketHolidaysStrip';
import { DailyNewsSummaryCard } from '@/components/news/DailyNewsSummaryCard';

const PAGE_SIZE = 24;

export default function NewsPage() {
  const { user } = useAuth();
  const tz = useTimezone();
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('All');
  const [page, setPage] = useState(1);
  const [sidebarSlot, setSidebarSlot] = useState<AdSlot | undefined>(undefined);
  const [infeedSlot, setInfeedSlot] = useState<AdSlot | undefined>(undefined);

  // Feed customizer state
  const [availableFeeds, setAvailableFeeds] = useState<NewsFeedPublic[]>([]);
  const [selectedFeedIds, setSelectedFeedIds] = useState<string[] | null>(null); // null = use defaults
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [customizerSaving, setCustomizerSaving] = useState(false);
  // Local toggle state inside customizer (before Apply)
  const [draftFeedIds, setDraftFeedIds] = useState<Set<string>>(new Set());

  const fetchNews = useCallback(async (feedIds?: string[] | null) => {
    setLoading(true);
    try {
      const data = await getNews(undefined, feedIds ?? undefined);
      setArticles(data);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch ad slots
    getAdSlots()
      .then((slots) => {
        setSidebarSlot(slots.find((s) => s.slotName === 'sidebar-right'));
        setInfeedSlot(slots.find((s) => s.slotName === 'in-feed' || s.slotName === 'news-infeed'));
      })
      .catch(() => {});

    // If logged in, load feed preferences + available feeds
    if (user) {
      Promise.all([getNewsFeeds(), getNewsFeedPreferences()])
        .then(([feeds, prefs]) => {
          setAvailableFeeds(feeds);
          const savedIds = prefs.feedIds;
          setSelectedFeedIds(savedIds);
          setDraftFeedIds(new Set(savedIds ?? feeds.filter((f) => f.enabled).map((f) => f.id)));
          fetchNews(savedIds);
        })
        .catch(() => fetchNews(null));
    } else {
      fetchNews(null);
    }
  }, [user, fetchNews]);

  const handleCustomizerOpen = () => {
    // Reset draft to current selection when opening
    if (selectedFeedIds) {
      setDraftFeedIds(new Set(selectedFeedIds));
    } else {
      setDraftFeedIds(new Set(availableFeeds.filter((f) => f.enabled).map((f) => f.id)));
    }
    setCustomizerOpen((o) => !o);
  };

  const toggleDraftFeed = (id: string) => {
    setDraftFeedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const applyFeedPreferences = async () => {
    const ids = Array.from(draftFeedIds);
    setCustomizerSaving(true);
    try {
      await saveNewsFeedPreferences(ids);
      setSelectedFeedIds(ids);
      setCustomizerOpen(false);
      fetchNews(ids);
    } catch {
      // ignore
    } finally {
      setCustomizerSaving(false);
    }
  };

  const resetFeedPreferences = async () => {
    setCustomizerSaving(true);
    try {
      await saveNewsFeedPreferences([]);
      setSelectedFeedIds(null);
      setDraftFeedIds(new Set(availableFeeds.filter((f) => f.enabled).map((f) => f.id)));
      setCustomizerOpen(false);
      fetchNews(null);
    } catch {
      // ignore
    } finally {
      setCustomizerSaving(false);
    }
  };

  const sources = useMemo(() => {
    const set = new Set(articles.map((a) => a.source));
    return ['All', ...Array.from(set).sort()];
  }, [articles]);

  const filtered = useMemo(() => {
    let items = articles;
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter(
        (a) =>
          a.headline.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.tickers.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (source !== 'All') items = items.filter((a) => a.source === source);
    return items;
  }, [articles, query, source]);

  const paged = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = filtered.length > paged.length;

  // Build grid items: interleave in-feed ads after every 4 articles
  const gridItems = useMemo(() => {
    const items: Array<{ type: 'article'; article: NewsArticle } | { type: 'ad'; key: string }> = [];
    paged.forEach((article, i) => {
      items.push({ type: 'article', article });
      if ((i + 1) % 8 === 0 && i + 1 < paged.length) {
        items.push({ type: 'ad', key: `ad-${i}` });
      }
    });
    return items;
  }, [paged]);

  return (
    <div className="px-2 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Market News</h1>
        <p className="text-fg-muted text-sm mt-1">Latest financial news and market updates</p>
      </div>

      {/* Market holidays */}
      <MarketHolidaysStrip />

      {/* AI-generated daily news digest (renders silently when absent) */}
      <DailyNewsSummaryCard />

      {/* Search, Filter & Customize */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" size={16} />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search news, tickers..."
            className="w-full bg-card-bg border border-default rounded-lg pl-9 pr-4 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
        </div>
        <select
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="bg-card-bg border border-default rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500"
        >
          {sources.map((s) => <option key={s}>{s}</option>)}
        </select>
        {user && availableFeeds.length > 0 && (
          <button
            onClick={handleCustomizerOpen}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors',
              customizerOpen
                ? 'bg-sky-500/10 border-sky-500/40 text-sky-400'
                : 'bg-card-bg border-default text-slate-300 hover:text-white hover:border-muted'
            )}
          >
            <Settings2 size={14} />
            Customize Feeds
            {customizerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>

      {/* Feed Customizer Panel */}
      {customizerOpen && availableFeeds.length > 0 && (
        <div className="mb-5 bg-card-bg border border-default rounded-xl p-4">
          <p className="text-xs text-fg-muted mb-3">Select which news feeds to include in your personal feed:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {availableFeeds.map((feed) => {
              const active = draftFeedIds.has(feed.id);
              return (
                <button
                  key={feed.id}
                  onClick={() => toggleDraftFeed(feed.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors font-medium',
                    active
                      ? 'bg-sky-500/15 border-sky-500/40 text-sky-300'
                      : 'bg-card-hover-bg border-default text-fg-muted hover:text-fg'
                  )}
                >
                  {active && <Check size={10} className="flex-shrink-0" />}
                  {feed.name}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={applyFeedPreferences}
              disabled={customizerSaving || draftFeedIds.size === 0}
              className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              {customizerSaving ? 'Applying\u2026' : 'Apply'}
            </button>
            <button
              onClick={resetFeedPreferences}
              disabled={customizerSaving}
              className="px-4 py-1.5 text-fg-muted hover:text-fg text-xs transition-colors"
            >
              Reset to Defaults
            </button>
            <button
              onClick={() => setCustomizerOpen(false)}
              className="px-4 py-1.5 text-fg-subtle hover:text-fg-muted text-xs transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Results count */}
      <p className="text-sm text-fg-subtle mb-4">
        {loading
          ? 'Loading articles\u2026'
          : <><span className="text-white">{paged.length}</span> of <span className="text-white">{filtered.length}</span> articles</>
        }
      </p>

      {/* News Grid + Sidebar */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-card-bg border border-default rounded-xl overflow-hidden animate-pulse">
              <div className="h-48 bg-card-hover-bg" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-border-default rounded w-1/3" />
                <div className="h-4 bg-border-default rounded w-full" />
                <div className="h-4 bg-border-default rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-fg-subtle">No news found matching your search.</div>
      ) : (
        <div className="flex gap-6 items-start">
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {gridItems.map((item) => {
                if (item.type === 'ad') {
                  return <InFeedAd key={item.key} slot={infeedSlot} />;
                }
                const article = item.article;
                return (
                  <a
                    key={article.id}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-card-bg border border-default rounded-xl overflow-hidden hover:bg-card-hover-bg hover:border-muted transition-all group relative"
                  >
                    <NewsImage id={article.id} imageUrl={article.imageUrl} className="float-right w-24 h-20 ml-2 mb-1 mt-3 mr-3 rounded-lg object-cover flex-shrink-0" />
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[13px] font-semibold text-fg-muted bg-card-hover-bg px-1.5 py-0.5 rounded">
                          {article.source}
                        </span>
                        <span className="text-[13px] text-slate-600">{timeAgo(article.publishedAt, tz)}</span>
                      </div>
                      <h3 className="text-base font-semibold text-slate-200 group-hover:text-white transition-colors leading-snug mb-1.5">
                        {article.headline}
                      </h3>
                      <p className="text-sm text-fg-subtle leading-relaxed mb-2 clear-right">
                        {article.summary}
                      </p>
                      {article.tickers.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-auto">
                          {article.tickers.map((t) => (
                            <span key={t} className="text-[10px] text-sky-400 bg-sky-500/10 px-1.5 py-0.5 rounded font-mono">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="px-6 py-2.5 bg-card-bg border border-default hover:bg-card-hover-bg hover:border-muted text-slate-300 hover:text-white text-sm font-medium rounded-xl transition-all"
                >
                  Load More
                </button>
              </div>
            )}
          </div>

          {/* Sidebar Ad */}
          <div className="hidden xl:block flex-shrink-0">
            <div className="sticky top-4">
              <SidebarAd slot={sidebarSlot} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
